import { useState, useEffect, useMemo } from 'react';
import { createTheme } from '@mui/material';
import { getBrandName } from '@/utils/helpers';
import { getLocationParam } from '@/utils/url';

export enum ThemeMode {
  light = 'light',
  dark = 'dark',
}

const lightColor = '#FFFFFF';
const darkColor = '#1C1F25';
const secondaryDarkColor = '#444854';

const getInitThemeMode = (): ThemeMode => {
  const brandsWithDefaultLightTheme = ['investico', 'invesacapital', '365trader'];
  // Theme default
  // INIT THEME HERE
  // check query param variant
  const urlParams = new URLSearchParams(window.location.search);
  const theme = urlParams.get("theme");
  if (theme && theme.toLowerCase() === ThemeMode.light) {
    return ThemeMode.light;
  } else if (theme && theme.toLowerCase() === ThemeMode.dark) {
    return ThemeMode.dark;
  }


  const brand = getBrandName();

  if (brandsWithDefaultLightTheme.includes(brand.toLocaleLowerCase())) {
    return ThemeMode.light;
  }

  return getLocationParam('theme') === ThemeMode.light ? ThemeMode.light : ThemeMode.dark;
};

export const useThemeMode = () => {
  const [mode, setMode] = useState<ThemeMode>(ThemeMode.dark);

  useEffect(() => {
    setMode(getInitThemeMode());
  }, []);

  useEffect(() => {
    document.documentElement.classList.add(`theme-${mode}`);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        const nextMode = mode === ThemeMode.dark ? ThemeMode.light : ThemeMode.dark;
        const htmlEl = document.documentElement;

        htmlEl.classList.add(`theme-${nextMode}`);
        htmlEl.classList.remove(`theme-${mode}`);

        setMode(nextMode);

        // trigger resize event to correctly fill the free space with the chart
        window.dispatchEvent(new Event('resize'));
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: mode === ThemeMode.light ? lightColor : darkColor },
          secondary: { main: mode === ThemeMode.light ? lightColor : secondaryDarkColor },
          background: {
            default: mode === ThemeMode.light ? lightColor : darkColor,
            paper: mode === ThemeMode.light ? lightColor : darkColor,
          },
        },
      }),
    [mode]
  );

  return { colorMode, theme };
};
